// Blender-style transform gizmo: three translate arrows (X/Y/Z), three
// rotate rings (X/Y/Z), and a center "free move" sphere — world-axis
// aligned, drawn at the selected object's center. Handles render on top
// (depthTest off) so they're always grabbable; arrows/rings keep their axis
// color and use opacity for hover/active feedback, the center goes yellow.
// This class only owns the handles + hit-testing + highlighting.
//
// Authored at unit scale around its own origin and parented to gridGroup;
// the caller positions it at the object center and scales the whole group.
// Orientation is GLOBAL, so the group is never rotated.

import * as THREE from "three";

// X red, Y green, Z blue (Blender's axis colors). Exported so the
// protractor can tint its feedback to match the grabbed ring.
export const AXIS_COLORS = [0xff5566, 0x4fcf4f, 0x5588ff];
const HOVER_COLOR = 0xffe04a; // yellow highlight for the center handle
const CENTER_COLOR = 0xdddde2; // neutral center "free move" handle
const CENTER_RADIUS = 0.13; // unit sphere radius at the origin

// Unit geometry (multiplied by the group scale the caller sets).
const SHAFT_LEN = 1.0;
// Start each shaft this far out from the origin (just past the center sphere) so
// the three arrow bases don't overlap the center handle — or each other — at the
// origin. Coincident depth-test-off, equal-renderOrder geometry there sorts
// unstably and flickers as the camera moves.
const SHAFT_GAP = 0.2;
const SHAFT_RADIUS = 0.022;
const CONE_LEN = 0.22;
const CONE_RADIUS = 0.075;
export const RING_RADIUS = 0.82; // exported so the protractor can size itself
const RING_TUBE = 0.025;
// In the 3D layout view a rotate ring renders as a half-arc — only the 180°
// nearest the camera, where every grab actually lands — so the gizmo reads less
// busy. setRingArc/setRingFull swap a ring between this arc and the full circle.
const RING_ARC = Math.PI;
// Invisible hit proxies are fatter than the visible geometry so the handles
// are easier to grab without making them look chunky.
const ARROW_PROXY_RADIUS = 0.1;
const ARROW_PROXY_LEN = 1.35; // covers shaft + cone (with a little slack)
const ARROW_PROXY_CENTER = 0.6;
const RING_PROXY_TUBE = 0.11;
const CENTER_PROXY_RADIUS = 0.22;

// "free" is the center handle — translate on all axes (view-plane move).
export type GizmoKind = "translate" | "rotate" | "free";

// Pick priority when overlapping fat hit-proxies are all hit: the center
// "free" handle wins at the origin, the rotate ring wins where an arrow
// crosses it, and the translate arrow takes the rest of its length.
const KIND_PRIORITY: Record<GizmoKind, number> = {
  free: 3,
  rotate: 2,
  translate: 1,
};
export interface GizmoHit {
  kind: GizmoKind;
  axis: number; // 0 = X, 1 = Y, 2 = Z; -1 for the center ("free") handle
  distance: number; // world-space ray distance
}

const _yUp = new THREE.Vector3(0, 1, 0);
const _zFwd = new THREE.Vector3(0, 0, 1);
const _axisVec = new THREE.Vector3();

function axisUnit(axis: number): THREE.Vector3 {
  return _axisVec.set(axis === 0 ? 1 : 0, axis === 1 ? 1 : 0, axis === 2 ? 1 : 0);
}

export class TransformGizmo {
  private group = new THREE.Group();
  private handles: THREE.Mesh[] = []; // visible geometry (display / hover / isolate)
  private hitProxies: THREE.Mesh[] = []; // invisible, fatter — raycast targets
  private proxyMat: THREE.MeshBasicMaterial;
  private translateMats: THREE.MeshBasicMaterial[] = [];
  private rotateMats: THREE.MeshBasicMaterial[] = [];
  private centerMat: THREE.MeshBasicMaterial;
  // Rotate rings + their hit proxies, indexed by axis, plus the full-circle and
  // half-arc geometries each can be pointed at (built once; swapping the
  // `geometry` reference per frame allocates nothing).
  private rotateRings: THREE.Mesh[] = [];
  private rotateProxies: THREE.Mesh[] = [];
  private fullRingGeo = new THREE.TorusGeometry(RING_RADIUS, RING_TUBE, 10, 48);
  private arcRingGeo = new THREE.TorusGeometry(RING_RADIUS, RING_TUBE, 10, 24, RING_ARC);
  private fullProxyGeo = new THREE.TorusGeometry(RING_RADIUS, RING_PROXY_TUBE, 8, 32);
  private arcProxyGeo = new THREE.TorusGeometry(RING_RADIUS, RING_PROXY_TUBE, 8, 16, RING_ARC);
  // Per-frame scratch for re-orienting an arc toward the camera (no allocation).
  private _rx = new THREE.Vector3();
  private _ry = new THREE.Vector3();
  private _rz = new THREE.Vector3();
  private _rm = new THREE.Matrix4();

  constructor() {
    this.group.visible = false;
    // Invisible-but-raycastable material for the hit proxies.
    this.proxyMat = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0,
      depthTest: false,
      depthWrite: false,
    });
    for (let axis = 0; axis < 3; axis++) {
      this.translateMats[axis] = this.makeMat(AXIS_COLORS[axis]);
      this.rotateMats[axis] = this.makeMat(AXIS_COLORS[axis]);
      this.buildArrow(axis);
      this.buildRing(axis);
    }
    // Center "free move" handle — a small sphere at the origin.
    this.centerMat = this.makeMat(CENTER_COLOR);
    const center = new THREE.Mesh(
      new THREE.SphereGeometry(CENTER_RADIUS, 16, 12),
      this.centerMat,
    );
    this.addHandle(center, "free", -1);
    this.addProxy(
      new THREE.Mesh(new THREE.SphereGeometry(CENTER_PROXY_RADIUS, 12, 8)),
      "free",
      -1,
    );
  }

  get object3D(): THREE.Object3D {
    return this.group;
  }

  private makeMat(color: number): THREE.MeshBasicMaterial {
    return new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.95,
      depthTest: false,
      depthWrite: false,
    });
  }

  private addHandle(mesh: THREE.Mesh, kind: GizmoKind, axis: number): void {
    mesh.userData.gizmoKind = kind;
    mesh.userData.gizmoAxis = axis;
    mesh.renderOrder = 980;
    this.group.add(mesh);
    this.handles.push(mesh);
  }

  // A fatter, invisible mesh that the raycast uses instead of the thin
  // visible geometry. `mesh` is positioned/oriented by the caller.
  private addProxy(mesh: THREE.Mesh, kind: GizmoKind, axis: number): void {
    mesh.material = this.proxyMat;
    mesh.userData.gizmoKind = kind;
    mesh.userData.gizmoAxis = axis;
    this.group.add(mesh);
    this.hitProxies.push(mesh);
  }

  private buildArrow(axis: number): void {
    const mat = this.translateMats[axis];
    const shaftLen = SHAFT_LEN - SHAFT_GAP;
    const shaft = new THREE.Mesh(
      new THREE.CylinderGeometry(SHAFT_RADIUS, SHAFT_RADIUS, shaftLen, 12),
      mat,
    );
    shaft.position.y = SHAFT_GAP + shaftLen / 2; // span [SHAFT_GAP, SHAFT_LEN] along +Y
    const cone = new THREE.Mesh(
      new THREE.ConeGeometry(CONE_RADIUS, CONE_LEN, 16),
      mat,
    );
    cone.position.y = SHAFT_LEN + CONE_LEN / 2;
    const arrow = new THREE.Group();
    arrow.add(shaft, cone);
    // Orient the +Y arrow onto its axis.
    arrow.quaternion.setFromUnitVectors(_yUp, axisUnit(axis).clone());
    // Re-parent the two meshes into the gizmo group with the arrow's
    // orientation baked in (so each mesh is a direct, raycastable handle).
    arrow.updateMatrix();
    for (const m of [shaft, cone]) {
      m.applyMatrix4(arrow.matrix);
      this.addHandle(m, "translate", axis);
    }
    // Fat invisible cylinder along the axis for an easier grab.
    const proxy = new THREE.Mesh(
      new THREE.CylinderGeometry(
        ARROW_PROXY_RADIUS,
        ARROW_PROXY_RADIUS,
        ARROW_PROXY_LEN,
        8,
      ),
    );
    proxy.position.copy(axisUnit(axis)).multiplyScalar(ARROW_PROXY_CENTER);
    proxy.quaternion.setFromUnitVectors(_yUp, axisUnit(axis).clone());
    this.addProxy(proxy, "translate", axis);
  }

  private buildRing(axis: number): void {
    // Start as a full circle (the modeling-style default); the floor slice swaps
    // it to a near-facing arc each frame via setRingArc/setRingFull.
    const ring = new THREE.Mesh(this.fullRingGeo, this.rotateMats[axis]);
    // Torus lies in XY (its axis is +Z); rotate so its axis matches.
    ring.quaternion.setFromUnitVectors(_zFwd, axisUnit(axis).clone());
    this.addHandle(ring, "rotate", axis);
    this.rotateRings[axis] = ring;
    // Fat invisible torus for an easier grab.
    const proxy = new THREE.Mesh(this.fullProxyGeo);
    proxy.quaternion.setFromUnitVectors(_zFwd, axisUnit(axis).clone());
    this.addProxy(proxy, "rotate", axis);
    this.rotateProxies[axis] = proxy;
  }

  // Shape a rotate ring (its visible mesh + hit proxy) as a half-arc whose near
  // 180° faces the camera. `camDir` is the gizmo-local direction toward the
  // camera — the gizmo group is never rotated, so a world-space camera direction
  // works directly. Visibility is the caller's job; this only sets geometry +
  // orientation. Falls back to a full circle if the camera is dead-on the axis
  // (the arc midpoint would be degenerate — but that's the axis-aligned case the
  // caller handles with setRingFull anyway).
  setRingArc(axis: number, camDir: THREE.Vector3): void {
    this._rz.set(axis === 0 ? 1 : 0, axis === 1 ? 1 : 0, axis === 2 ? 1 : 0);
    // Arc midpoint = local +Y = camera direction projected into the ring plane.
    this._ry.copy(camDir).addScaledVector(this._rz, -camDir.dot(this._rz));
    if (this._ry.lengthSq() < 1e-8) {
      this.setRingFull(axis);
      return;
    }
    this._ry.normalize();
    this._rx.crossVectors(this._ry, this._rz).normalize();
    this._rm.makeBasis(this._rx, this._ry, this._rz);
    const ring = this.rotateRings[axis];
    const proxy = this.rotateProxies[axis];
    ring.geometry = this.arcRingGeo;
    proxy.geometry = this.arcProxyGeo;
    ring.quaternion.setFromRotationMatrix(this._rm);
    proxy.quaternion.copy(ring.quaternion);
  }

  // Shape a rotate ring as the full circle (axis-aligned view, 2D plan view, or
  // an active rotate-drag). Visibility is the caller's job.
  setRingFull(axis: number): void {
    this._rz.set(axis === 0 ? 1 : 0, axis === 1 ? 1 : 0, axis === 2 ? 1 : 0);
    const ring = this.rotateRings[axis];
    const proxy = this.rotateProxies[axis];
    ring.geometry = this.fullRingGeo;
    proxy.geometry = this.fullProxyGeo;
    ring.quaternion.setFromUnitVectors(_zFwd, this._rz);
    proxy.quaternion.copy(ring.quaternion);
  }

  // Place at `centerLocal` (gridGroup-local) and scale the whole gizmo.
  setTarget(centerLocal: THREE.Vector3, scale: number): void {
    this.group.position.copy(centerLocal);
    this.group.scale.setScalar(scale);
    this.group.visible = true;
  }

  hide(): void {
    this.group.visible = false;
  }

  // Visit every part (visible handle + its invisible hit-proxy) so visibility
  // changes affect raycasting (the proxy pick skips hidden parts) as well as
  // display.
  private forEachPart(fn: (m: THREE.Mesh) => void): void {
    for (const h of this.handles) fn(h);
    for (const p of this.hitProxies) fn(p);
  }

  // Show only the handle matching (kind, axis); hide the rest. Used while a
  // constrained drag is active so the other arrows/rings get out of the way.
  isolateHandle(kind: GizmoKind, axis: number): void {
    this.forEachPart((m) => {
      m.visible =
        m.userData.gizmoKind === kind && m.userData.gizmoAxis === axis;
    });
  }

  showAllHandles(): void {
    this.forEachPart((m) => {
      m.visible = true;
    });
  }

  // Show/hide a specific handle + its proxy (e.g. the floor gizmo hides the
  // vertical-translate arrow and X/Z rotate rings in the 2D plan view).
  setHandleVisible(kind: GizmoKind, axis: number, v: boolean): void {
    this.forEachPart((m) => {
      if (m.userData.gizmoKind === kind && m.userData.gizmoAxis === axis) {
        m.visible = v;
      }
    });
  }

  // Grabbable handle under the ray, resolved by PRIORITY so the overlapping
  // fat hit-proxies behave predictably: the center "free" handle wins at the
  // origin, the rotate ring wins where an arrow crosses it, and the translate
  // arrow wins along the rest of its visible length. Only VISIBLE proxies are
  // tested, so the 2D mask's hidden handles can't be grabbed. Tie-break by
  // distance. Returns null if nothing is hit.
  raycast(raycaster: THREE.Raycaster): GizmoHit | null {
    if (!this.group.visible) return null;
    this.group.updateWorldMatrix(true, true);
    const hits = raycaster.intersectObjects(this.hitProxies, false);
    if (hits.length === 0) return null;
    let best: GizmoHit | null = null;
    let bestPri = -1;
    let bestDist = Infinity;
    for (const h of hits) {
      // Raycaster ignores Object3D.visible, so enforce the mask manually: a
      // hidden proxy (e.g. the X/Z rings in the 2D plan view) isn't grabbable.
      if (!h.object.visible) continue;
      const kind = h.object.userData.gizmoKind as GizmoKind;
      const pri = KIND_PRIORITY[kind];
      if (pri > bestPri || (pri === bestPri && h.distance < bestDist)) {
        bestPri = pri;
        bestDist = h.distance;
        best = {
          kind,
          axis: h.object.userData.gizmoAxis as number,
          distance: h.distance,
        };
      }
    }
    return best;
  }

  // Update handle appearance from the current hover + active(dragged) state.
  // Arrows AND rings keep their axis color always (never yellow); feedback
  // is by opacity — slightly translucent normally, a touch more on hover,
  // fully solid while being dragged. The center sphere keeps the yellow
  // hover/active highlight.
  applyHighlight(hover: GizmoHit | null, active: GizmoHit | null): void {
    const is = (h: GizmoHit | null, k: GizmoKind, a: number) =>
      h?.kind === k && h.axis === a;
    const op = (activeHit: boolean, hoverHit: boolean) =>
      activeHit ? 1.0 : hoverHit ? 0.8 : 0.5;
    for (let axis = 0; axis < 3; axis++) {
      this.translateMats[axis].color.setHex(AXIS_COLORS[axis]);
      this.translateMats[axis].opacity = op(
        is(active, "translate", axis),
        is(hover, "translate", axis),
      );
      this.rotateMats[axis].color.setHex(AXIS_COLORS[axis]);
      this.rotateMats[axis].opacity = op(
        is(active, "rotate", axis),
        is(hover, "rotate", axis),
      );
    }
    const cHot = hover?.kind === "free" || active?.kind === "free";
    this.centerMat.color.setHex(cHot ? HOVER_COLOR : CENTER_COLOR);
  }
}
